import { memo, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { Grid, AgGrid, Flex } from "@jsq/ds/core";
import NoRowsOverlay from "~/pages/Compliance/Common/NoRowsOverlay";
import { useComplianceProfileRefreshTab, useDocumentRefreshFeatureFlag } from "../flags";
import { dashboardContainer } from "../styles_vanilla.css";
import { profilesColumnDefs } from "./columnDefinitions";
import { ProfilesGraphqlDataSource } from "./profilesGraphqlDataSource";
import { ProfileRowItem } from "./types";

const DEFAULT_PAGE_SIZE = 20;

export const ProfilesTable = memo(() => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const profileId = searchParams.get("profileId");
  const profileName = searchParams.get("name");
  const enableProfileRefresh = useComplianceProfileRefreshTab();
  const enableDocumentRefresh = useDocumentRefreshFeatureFlag();

  const profilesDataSource = useMemo(
    () => new ProfilesGraphqlDataSource(profileId ? parseInt(profileId) : undefined),
    [profileId],
  );

  const filteredColumnDefs = useMemo(() => {
    let columns = profilesColumnDefs;

    if (!enableProfileRefresh) {
      columns = columns.filter(
        (col) => !["lastRefresh", "nextRefresh", "refreshStatus"].includes(col.field as string),
      );
    }

    if (!enableDocumentRefresh) {
      columns = columns.filter((col) => col.field !== "documentRefreshStatus");
    }

    return columns;
  }, [enableProfileRefresh, enableDocumentRefresh]);

  const onGridReady = useCallback(
    (event: AgGrid.GridReadyEvent<ProfileRowItem>) => {
      if (!profileName) return;
      event.api.setFilterModel({
        name: {
          filterType: "text",
          type: "contains",
          filter: profileName,
        },
      });
    },
    [profileName],
  );

  const gridOptions: AgGrid.GridOptions<ProfileRowItem> = useMemo(
    () => ({
      autoSizeStrategy: { type: "fitGridWidth" },
      rowHeight: 42,
      suppressCellFocus: true,
      rowModelType: "serverSide",
      serverSideDatasource: profilesDataSource,
      serverSideStoreType: "partial",
      cacheBlockSize: DEFAULT_PAGE_SIZE,
      getRowId: (params: AgGrid.GetRowIdParams<ProfileRowItem>) => params.data.id?.toString(),
      pagination: true,
      paginationPageSize: DEFAULT_PAGE_SIZE,
      paginationPageSizeSelector: false,
      suppressPaginationPanel: false,
    }),
    [profilesDataSource],
  );

  return (
    <Flex className={dashboardContainer}>
      <Grid<ProfileRowItem>
        gridOptions={gridOptions}
        columnDefs={filteredColumnDefs}
        onGridReady={onGridReady}
        suppressColumnVirtualisation={true}
        suppressRowVirtualisation={true}
        noRowsOverlayComponent={NoRowsOverlay}
        noRowsOverlayComponentParams={{ message: "No profiles found" }}
      />
    </Flex>
  );
});

ProfilesTable.displayName = "ProfilesTable";
